<template>
  <div class="agency-event-ranking">
    <div class="agency-event-ranking" v-if="showPage">
      <alert
        :alertContent="alertContent"
        @alertButtonClick="alertButtonClick"
        v-if="shouldShowAlert"
      ></alert>

      <!-- イベント情報ヘッダー -->
      <agency-event-ranking-header :eventInfo="eventInfo" />

      <!-- タブ表示 -->
      <div class="tab-container">
        <div class="tab-header">
          <div
            class="tab-item"
            :class="{ active: currentTab === 'individual' }"
            @click="switchTab('individual')"
          >
            {{ $t('agencyEventTabIndividual') }}
          </div>
          <div
            v-if="eventInfo.eventType === AgencyEventType.AGENCY_EVENT_TYPE_GROUP"
            class="tab-item"
            :class="{ active: currentTab === 'group' }"
            @click="switchTab('group')"
          >
            {{ $t('agencyEventTabGroup') }}
          </div>
        </div>
      </div>

      <!-- ランキング表示 -->
      <div class="ranking-container">
        <agency-event-ranking-list
          v-if="currentTab === 'individual'"
          :rankingList="individualRankingList"
          :eventKey="eventKey"
          :rankingType="'individual'"
          @scrolledToBottom="loadMoreRanking"
        ></agency-event-ranking-list>
        
        <div v-if="currentTab === 'group'">
          <!-- グループ内個人ランキング表示時の戻るリンク -->
          <div v-if="showGroupIndividualRanking" class="back-to-group-list">
            <div class="back-link" @click="backToGroupList">
              ← {{ $t('agencyEventBackToGroupList') }}
            </div>
          </div>
          
          <!-- グループランキング表示 -->
          <agency-event-ranking-list
            v-if="!showGroupIndividualRanking"
            :rankingList="groupRankingList"
            :eventKey="eventKey"
            :rankingType="'group'"
            @scrolledToBottom="loadMoreRanking"
            @openGroupIndividualRanking="handleOpenGroupIndividualRanking"
          ></agency-event-ranking-list>
          
          <!-- グループ内個人ランキング表示 -->
          <agency-event-ranking-list
            v-if="showGroupIndividualRanking"
            :rankingList="groupIndividualRankingList"
            :eventKey="eventKey"
            :rankingType="'individual'"
            :groupName="
              currentGroupInfo?.groupName ||
              $t('agencyEventGroupIndividualRankingDefaultGroupName')
            "
            @scrolledToBottom="loadMoreRanking"
          ></agency-event-ranking-list>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { apiV2 } from '@/api/appGatewayServerV2Api'
import Alert from '@/components/Alert.vue'
import AlertFactory from '@/utils/AlertFactory'
import AgencyEventRankingList from '@/components/AgencyEventRanking/AgencyEventRankingList.vue'
import AgencyEventRankingHeader from '@/components/AgencyEventRanking/AgencyEventRankingHeader.vue'
import { AgencyEventType } from '@/api/pb/AgencyEventRanking/AgencyEventRankingGetEvent'
import { LanguageQueryMixin } from '@/mixins/LanguageQuery'

export default {
  components: {
    Alert,
    AgencyEventRankingList,
    AgencyEventRankingHeader,
  },
  mixins: [LanguageQueryMixin],
  name: 'AgencyEventRanking',
  data: function () {
    return {
      eventKey: undefined,
      eventInfo: null,
      currentTab: 'individual',
      individualRankingList: [],
      groupRankingList: [],
      groupIndividualRankingList: [],
      currentGroupInfo: null,
      showGroupIndividualRanking: false,
      shouldShowAlert: false,
      alertContent: {},
      processing: false,
      hasMoreData: true,
      AgencyEventType: AgencyEventType,
    }
  },
  computed: {
    showPage: function () {
      return this.eventKey !== undefined && this.eventInfo !== null
    },
    eventName: function () {
      return this.eventInfo?.name || this.$t('agencyEventDefaultEventName')
    }
  },
  watch: {
    shouldShowAlert: function (val) {
      this.allowScrolling(!val)
    },
    eventName: function (eventName) {
      document.title = eventName
    },
  },
  created() {
    this.eventKey = this.$route.params.eventKey
    document.title = this.eventName
    this.allowScrolling(true)
    this.fetchEventInfo()
  },
  methods: {
    handleTouchMove: function (e) {
      e.preventDefault()
    },
    allowScrolling: function (val) {
      if (val) {
        document.body.className = 'body__scrolling'
        document.ontouchmove = () => true
      } else {
        document.body.className = 'body__nonscrolling'
        document.ontouchmove = (e) => e.preventDefault()
      }
    },
    alertButtonClick: function (index) {
      this.alertContent.alertItems[index].action()
    },
    switchTab(tabType) {
      if (this.currentTab === tabType) {
        return
      }
      
      this.currentTab = tabType
      
      if (tabType === 'individual' && this.individualRankingList.length === 0) {
        this.fetchIndividualRankingData()
      } else if (tabType === 'group' && this.groupRankingList.length === 0) {
        this.fetchGroupRankingData()
      }
    },
    async fetchEventInfo() {
      try {
        // 事務所イベント情報取得API(/agency_event/ranking/get_event)を呼び出し
        const response = await apiV2().agencyEvent.ranking.getEvent({
          eventKey: this.eventKey,
        })

        if (response) {
          this.eventInfo = response

          // currentTabの初期値はindividual
          this.currentTab = 'individual'

          // イベント情報取得成功時のみ個人ランキングデータを取得
          this.fetchIndividualRankingData()
        }
      } catch (error) {
        this.shouldShowAlert = true
        this.alertContent = AlertFactory.makeErrorContent(
          () => {
            this.shouldShowAlert = false
          },
          this.$t('agencyEventFetchEventInfoFailed')
        )
      }
    },
    async fetchIndividualRankingData() {
      if (this.processing) {
        return
      }

      this.processing = true

      try {
        // 事務所イベント個人ランキングAPI(/agency_event/ranking/get_individual)を呼び出し
        const response = await apiV2().agencyEvent.ranking.getIndividual({
          eventKey: this.eventKey,
        })

        if (response && response.rankingList) {
          this.individualRankingList = response.rankingList
        } else {
          this.individualRankingList = []
        }
      } catch (error) {
        this.shouldShowAlert = true
        this.alertContent = AlertFactory.makeErrorContent(
          () => {
            this.shouldShowAlert = false
          },
          this.$t('agencyEventFetchIndividualRankingFailed')
        )
      } finally {
        this.processing = false
      }
    },
    async fetchGroupRankingData() {
      if (this.processing) {
        return
      }

      this.processing = true

      try {
        // 事務所イベントグループランキングAPI(/agency_event/ranking/get_groups)を呼び出し
        const response = await apiV2().agencyEvent.ranking.getGroups({
          eventKey: this.eventKey,
        })

        if (response && response.rankingList) {
          this.groupRankingList = response.rankingList
        } else {
          this.groupRankingList = []
        }
      } catch (error) {
        this.shouldShowAlert = true
        this.alertContent = AlertFactory.makeErrorContent(
          () => {
            this.shouldShowAlert = false
          },
          this.$t('agencyEventFetchGroupRankingFailed')
        )
      } finally {
        this.processing = false
      }
    },
    async fetchGroupIndividualRankingData(groupId) {
      if (this.processing) {
        return
      }

      this.processing = true

      try {
        // 事務所イベントグループ内個人ランキングAPI(/agency_event/ranking/group/get_individual)を呼び出し
        const response = await apiV2().agencyEvent.ranking.group.getIndividual({
          eventKey: this.eventKey,
          groupId: groupId,
        })

        if (response && response.rankingList) {
          this.groupIndividualRankingList = response.rankingList
        } else {
          this.groupIndividualRankingList = []
        }
      } catch (error) {
        this.shouldShowAlert = true
        this.alertContent = AlertFactory.makeErrorContent(
          () => {
            this.shouldShowAlert = false
          },
          this.$t('agencyEventFetchGroupIndividualRankingFailed')
        )
      } finally {
        this.processing = false
      }
    },
    handleOpenGroupIndividualRanking(groupInfo) {
      this.currentGroupInfo = groupInfo
      this.showGroupIndividualRanking = true
      this.fetchGroupIndividualRankingData(groupInfo.groupId)
    },
    backToGroupList() {
      this.showGroupIndividualRanking = false
      this.currentGroupInfo = null
      this.groupIndividualRankingList = []
    },
    loadMoreRanking() {
      // 現在の実装では追加読み込みは不要（全データを一度に取得）
      // 必要に応じて将来的にページネーション機能を追加可能
    },
  },
}
</script>

<style scoped>
.agency-event-ranking {
  height: 100%;
  min-height: 850px;
}

.tab-container {
  max-width: 500px;
  margin: 0 auto;
}

.tab-header {
  display: flex;
  height: 42px;
  border-bottom: 1px solid #eeeeee;
  background-color: #fafafa;
}

.tab-item {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: #808080;
  cursor: pointer;
  border-bottom: 3px solid transparent;
  transition: all 0.2s ease;
}

.tab-item.active {
  color: #188dff;
  border-bottom-color: #188dff;
  font-weight: 600;
}

.ranking-container {
  max-width: 500px;
  margin: 0 auto;
}

.back-to-group-list {
  max-width: 500px;
  margin: 0 auto;
  background-color: #fafafa;
  border-bottom: 1px solid #eeeeee;
  padding: 12px 16px;
}

.back-link {
  color: #188dff;
  font-size: 14px;
  cursor: pointer;
  margin-bottom: 8px;
}

.back-link:hover {
  text-decoration: underline;
}

.group-title {
  font-size: 16px;
  font-weight: bold;
  color: #262626;
}
</style>
